import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  IconButton
} from '@mui/material';
import { Alert } from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  LocalShipping as DeliveryIcon,
  Payment as PaymentIcon,
  Schedule as TimeIcon
} from '@mui/icons-material';
import { Order } from '../models/Order';
import productService from '../services/productService';
import OrderUtils from '../utils/orderUtils';
import DateUtils from '../utils/dateUtils';
import {
  extractCustomizationSnapshotLabels,
  extractLegacyCustomizationLabelsSync,
  resolveLegacyCustomizationLabels
} from '../utils/orderCustomizationDisplay';
import type { Customer } from '../models/Customer';

interface OrderDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order | null;
  onOpenCustomerProfile?: (customer: Customer) => void;
}

const OrderDetailsDialog: React.FC<OrderDetailsDialogProps> = ({
  open,
  onClose,
  order,
  onOpenCustomerProfile
}) => {
  const [resolvedCustoms, setResolvedCustoms] = React.useState<Record<number, string[]>>({});

  const resolveEntries = React.useCallback(async (entries: any[]): Promise<string[]> => {
    const filtered = Array.isArray(entries) ? entries.filter((x: any) => x !== null && x !== undefined && String(x).trim() !== '') : [];
    const results = await Promise.all(filtered.map(async (val: any) => {
      const s = String(val);
      const looksId = /^[A-Za-z0-9]{20}$/.test(s);
      if (looksId) {
        try {
          const prod = await productService.getProductById(s);
          if (prod) {
            const cat = prod.category ? prod.category.toUpperCase() : 'SUPPLÉMENT';
            return `${cat} - ${prod.name}`;
          }
        } catch (e) {
          console.error('Erreur résolution produit', s, e);
        }
      }
      return s;
    }));
    return results;
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!order) return;
      const map: Record<number, string[]> = {};
      await Promise.all(order.items.map(async (item: any, idx: number) => {
        const snapshotCustoms = extractCustomizationSnapshotLabels(item);
        if (snapshotCustoms.length > 0) {
          map[idx] = snapshotCustoms;
          return;
        }

        map[idx] = await resolveLegacyCustomizationLabels(item, resolveEntries);
      }));
      if (!cancelled) setResolvedCustoms(map);
    };
    run();
    return () => { cancelled = true; };
  }, [order, resolveEntries]);

  if (!order) return null;

  const cancelledAt = OrderUtils.getCancellationTimestamp(order);
  const cancellationCause = OrderUtils.getCancellationCause(order);

  const getItemDescription = (item: any, index: number) => {
    // Utiliser le nom du produit depuis productNames si disponible
    const rawProductName = order.productNames && order.productNames[index]
      ? order.productNames[index].replace(/^\d+x\s*/, '') // Enlever la quantité du début
      : item.productName || item.name || 'Produit inconnu';
    const itemSize = typeof item.size === 'string' ? item.size.trim() : '';
    const productName = itemSize && itemSize.toLowerCase() !== 'normal'
      ? rawProductName.replace(new RegExp(`\\s*\\(${itemSize.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)\\s*$`, 'i'), '').trim()
      : rawProductName.trim();
    
    let description = `${item.quantity}x ${productName}`;
    
    // Ajouter la taille si disponible et différente de normal
    if (itemSize && itemSize.toLowerCase() !== 'normal') {
      description += ` (${itemSize.toUpperCase()})`;
    }
    
    // Affichage des personnalisations résolues si disponibles
    const resolved = resolvedCustoms[index];
    if (resolved && resolved.length > 0) {
      return `${description} (${resolved.join(', ')})`;
    }
    
    const snapshotCustoms = extractCustomizationSnapshotLabels(item);
    if (snapshotCustoms.length > 0) {
      description += ` (${snapshotCustoms.join(', ')})`;
      return description;
    }

    const customizations = extractLegacyCustomizationLabelsSync(item);
    if (customizations.length > 0) {
      description += ` (${customizations.join(', ')})`;
    }
    
    return description;
  };

  const formatTimestamp = (timestamp: Date | undefined) => {
    if (!timestamp) return 'Non défini';
    return DateUtils.formatDateTimeFR(timestamp);
  };

  const openAddressOnMap = () => {
    if (!order) return;
    const loc: any = order.clientAddress?.location;
    let lat: number | undefined;
    let lng: number | undefined;

    if (Array.isArray(loc)) {
      lat = loc[0];
      lng = loc[1];
    } else if (loc && typeof loc === 'object') {
      if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
        lat = loc.lat;
        lng = loc.lng;
      } else if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
        lat = loc.latitude;
        lng = loc.longitude;
      } else if (typeof (loc as any)._lat === 'number' && typeof (loc as any)._long === 'number') {
        lat = (loc as any)._lat;
        lng = (loc as any)._long;
      }
    }

    const hasCoords =
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      !(lat === 0 && lng === 0);

    if (hasCoords) {
      const url = `https://www.google.com/maps?q=${lat},${lng}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const formatted = order.clientAddress?.formatted || '';
    if (formatted) {
      const url = `https://www.google.com/maps?q=${encodeURIComponent(formatted)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const openCustomerProfile = () => {
    if (!order || !order.userId) return;
    const name = (order.clientName || '').trim();
    const parts = name.length ? name.split(' ') : ['Client'];
    const firstName = parts[0] || 'Client';
    const lastName = parts.slice(1).join(' ');

    const formatted = order.clientAddress?.formatted || '';
    const addrParts = formatted.split(',').map(p => p.trim()).filter(Boolean);
    const street = addrParts[0] || formatted || '';
    const city = addrParts[1] || '';
    const region = addrParts[2];
    const postalCode = addrParts[3];
    let latitude = 0;
    let longitude = 0;
    const loc: any = order.clientAddress?.location;
    if (Array.isArray(loc)) {
      latitude = typeof loc[0] === 'number' ? loc[0] : 0;
      longitude = typeof loc[1] === 'number' ? loc[1] : 0;
    } else if (loc && typeof loc === 'object') {
      if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
        latitude = loc.lat;
        longitude = loc.lng;
      } else if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
        latitude = loc.latitude;
        longitude = loc.longitude;
      } else if (typeof (loc as any)._lat === 'number' && typeof (loc as any)._long === 'number') {
        latitude = (loc as any)._lat;
        longitude = (loc as any)._long;
      }
    }

    const customer: Customer = {
      id: order.userId,
      firstName,
      lastName,
      phone: order.clientPhone || '',
      email: order.clientEmail || undefined,
      addresses: [
        {
          id: 'order-address',
          street,
          city,
          region,
          postalCode,
          details: undefined,
          latitude,
          longitude,
          isDefault: true
        }
      ]
    };
    if (onOpenCustomerProfile) {
      onOpenCustomerProfile(customer);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Box>
          <Typography variant="h6" component="div">
            Détails de la commande #{order.id.slice(-8)}
          </Typography>
          <Chip 
            label={OrderUtils.getOrderStatusLabel(order)}
            size="small"
            sx={{
              backgroundColor: OrderUtils.getOrderStatusColor(order),
              color: 'white',
              mt: 1
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {order.status === 'CANCELLED' && (
            <Alert severity="error" icon={false}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                Commande annulée — {cancellationCause}
              </Typography>
              {cancelledAt && (
                <Typography variant="caption" color="text.secondary">
                  Annulée le {DateUtils.formatDateTimeFR(cancelledAt)}
                </Typography>
              )}
            </Alert>
          )}
          {order.status === 'PAYMENT_FAILED' && (
            <Alert severity="warning">
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {OrderUtils.getPaymentIssueLabel(order)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cette commande n’a pas été confirmée pour le restaurant.
                {order.cmiReturnCode ? ` Code CMI : ${order.cmiReturnCode}.` : ''}
              </Typography>
            </Alert>
          )}
          {/* Première ligne - Informations client et magasin */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Informations client */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Paper elevation={1} sx={{ p: 2, height: 'fit-content' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  Informations Client
                </Typography>
                <Box sx={{ ml: 4 }}>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    sx={{ color: order.userId ? '#1976d2' : 'text.primary', textDecoration: order.userId ? 'underline' : 'none', cursor: order.userId ? 'pointer' : 'default' }}
                    onClick={openCustomerProfile}
                  >
                    {order.clientName || 'Client inconnu'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {order.clientPhone || 'Non disponible'}
                    </Typography>
                  </Box>
                  {order.clientAddress.formatted && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1, cursor: 'pointer' }}
                      onClick={openAddressOnMap}
                      title="Ouvrir dans Google Maps"
                    >
                      <LocationIcon fontSize="small" color="error" sx={{ mt: 0.2 }} />
                      <Typography component="div" variant="body2" sx={{ color: '#1976d2', textDecoration: 'underline' }}>
                        {order.clientAddress.formatted}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>

            {/* Informations magasin */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Paper elevation={1} sx={{ p: 2, height: 'fit-content' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StoreIcon color="primary" />
                  Magasin
                </Typography>
                <Box sx={{ ml: 4 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {order.storeName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                    <LocationIcon fontSize="small" color="action" sx={{ mt: 0.2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {order.storeAddress.formatted}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Produits commandés */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Produits commandés
            </Typography>
            <List dense>
              {order.items.map((item, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemText
                    primary={getItemDescription(item, index)}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Deuxième ligne - Livraison et paiement */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Informations de livraison */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeliveryIcon color="primary" />
                  Livraison
                </Typography>
                <Box sx={{ ml: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Méthode: {OrderUtils.getDeliveryMethodLabel(order.deliveryMethod)}
                  </Typography>
                  {order.deliveryPerson && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Livreur: {OrderUtils.getDriverName(order)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Téléphone: {order.deliveryPerson.phone}
                      </Typography>
                    </>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Distance estimée: {order.estimatedDistanceKm} km
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Durée estimée: {order.estimatedDurationMinutes} min
                  </Typography>
                </Box>
              </Paper>
            </Box>

            {/* Informations de paiement */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon color="primary" />
                  Paiement
                </Typography>
                <Box sx={{ ml: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Méthode: {OrderUtils.getPaymentMethodLabel(order.paymentMethod)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Sous-total: {OrderUtils.formatAmount(order.subTotal)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Frais de livraison: {OrderUtils.formatAmount(order.deliveryFeeAtOrderTime)}
                  </Typography>
                  {order.promoAmountApplied > 0 && (
                    <>
                      <Typography variant="body2" color="success.main">
                        Réduction: -{OrderUtils.formatAmount(order.promoAmountApplied)}
                      </Typography>
                      {!!order.promoCodeApplied && (
                        <Typography variant="body2" color="success.main">
                          Code promo: {order.promoCodeApplied}
                        </Typography>
                      )}
                    </>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body1" fontWeight="bold">
                    Total: {OrderUtils.formatAmount(order.totalAmount)}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Chronologie */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon color="primary" />
              Chronologie
            </Typography>
            <List dense>
              {order.statusTimestamps.placedAt && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Commande passée"
                    secondary={formatTimestamp(order.statusTimestamps.placedAt)}
                  />
                </ListItem>
              )}
              {order.statusTimestamps.acceptedAt && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Commande acceptée"
                    secondary={formatTimestamp(order.statusTimestamps.acceptedAt)}
                  />
                </ListItem>
              )}
              {order.statusTimestamps.preparingAt && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Préparation commencée"
                    secondary={formatTimestamp(order.statusTimestamps.preparingAt)}
                  />
                </ListItem>
              )}
              {order.statusTimestamps.readyForPickupAt && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Prêt pour récupération"
                    secondary={formatTimestamp(order.statusTimestamps.readyForPickupAt)}
                  />
                </ListItem>
              )}
              {order.statusTimestamps.acceptedByDriverAt && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Accepté par le livreur"
                    secondary={formatTimestamp(order.statusTimestamps.acceptedByDriverAt)}
                  />
                </ListItem>
              )}
              {order.statusTimestamps.deliveringAt && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="En cours de livraison"
                    secondary={formatTimestamp(order.statusTimestamps.deliveringAt)}
                  />
                </ListItem>
              )}
              {order.statusTimestamps.deliveredAt && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Livré"
                    secondary={formatTimestamp(order.statusTimestamps.deliveredAt)}
                  />
                </ListItem>
              )}
              {cancelledAt && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Annulée"
                    secondary={formatTimestamp(cancelledAt)}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsDialog;
